import React, { useState, useEffect, useCallback } from 'react';
import './App.css';

function App() {
  const [formData, setFormData] = useState({
    // Project Details
    builderName: '',
    projectName: '',
    projectLocation: '',
    city: '',
    zip: '30016',
    assetType: 'SFR',
    loanType: 'Ground Up',
    unitCount: 1,
    permitStatus: 'Permit In-Hand',
    
    // Financial Inputs
    refinanceCosts: 4096120,
    totalEquity: 1200000,
    equitySource: 1200000,
    desiredLTC: 0.825,
    arv: 5300000,
    
    // Construction Costs
    landCost: 1200000,
    softCosts: 235000,
    hardCosts: 2661120,
    contractorFee: 0,
    contingencyAllowance: 0,
    titleClosingAllowance: 3000,
    
    // Financing Terms
    originationPoints: 0.02,
    loanInterestRate: 0.12,
    term: 12,
    utilizationRate: 0.6,
    
    // Borrower Info
    creditScore: 660,
    grossLiquidity: 0,
    statedNetWorth: 0,
    experienceLevel: '',
    projectsCompleted: 0,
    timeInBusiness: 0
  });

  const [calculations, setCalculations] = useState({});

  const calculateValues = useCallback(() => {
    const {
      refinanceCosts,
      totalEquity,
      desiredLTC,
      arv,
      titleClosingAllowance,
      originationPoints,
      loanInterestRate,
      term
    } = formData;

    // Core calculations
    const constructionCost = refinanceCosts;
    const totalEquityNeeded = constructionCost * (1 - desiredLTC);
    const additionalEquityNeeded = totalEquityNeeded - totalEquity;
    const projectLTC = constructionCost > 0 ? (constructionCost - totalEquity) / constructionCost : 0;
    const fundingGap = constructionCost - totalEquity;
    
    // Financing calculations 
    // The loan amount calculation appears to be: funding gap + origination fees + interest reserve
    // Where origination fees and interest reserve are calculated on the total loan amount
    // This creates a circular dependency, so we solve: Loan = FundingGap / (1 - OriginationRate - InterestReserveRate)
    const interestReserveRate = (loanInterestRate * term) / 12;
    const estimatedLoanAmount = fundingGap / (1 - originationPoints - interestReserveRate);
    
    const originationFees = estimatedLoanAmount * originationPoints;
    const interestReserve = estimatedLoanAmount * interestReserveRate;
    const estimatedFinancingCosts = originationFees + (titleClosingAllowance || 0);
    const totalLoanCosts = estimatedFinancingCosts;
    
    // Total project cost
    const totalProjectCost = constructionCost + totalLoanCosts;
    
    // Revenue calculations
    const grossProjectRevenue = arv;
    const estimatedProjectRevenue = grossProjectRevenue - totalProjectCost;
    const estimatedProfitMargin = grossProjectRevenue > 0 ? estimatedProjectRevenue / grossProjectRevenue : 0;
    
    // LTC/LTV calculations
    const financedLTC = totalProjectCost > 0 ? estimatedLoanAmount / totalProjectCost : 0;
    const financedLTV = arv > 0 ? estimatedLoanAmount / arv : 0;
    
    // CoFi fees
    const docFee = 1000;
    const drawFeePerUnit = 250 * formData.unitCount;
    const legalUWFee = 500;
    const cofiFees = docFee + drawFeePerUnit + legalUWFee;
    
    // Advance day 1
    const cofiAdvanceDay1 = titleClosingAllowance || 3000;
    
    setCalculations({
      constructionCost,
      totalEquityNeeded,
      additionalEquityNeeded,
      projectLTC,
      fundingGap,
      estimatedLoanAmount,
      originationFees,
      interestReserve,
      estimatedFinancingCosts,
      totalLoanCosts,
      totalProjectCost,
      grossProjectRevenue,
      estimatedProjectRevenue,
      estimatedProfitMargin,
      financedLTC,
      financedLTV,
      cofiFees,
      cofiAdvanceDay1
    });
  }, [formData]);

  useEffect(() => {
    calculateValues();
  }, [calculateValues]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value || 0);
  };

  const formatPercentage = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'percent',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value || 0);
  };

  return (
    <div className="App">
      <header className="app-header">
        <h1>CoFi Capital Calculator</h1>
        <p>Construction Loan Analysis Tool</p>
      </header>

      <div className="calculator-container">
        <div className="input-section">
          <h2>Project Details</h2>
          <div className="form-group">
            <label>Builder Name:</label>
            <input
              type="text"
              value={formData.builderName}
              onChange={(e) => handleInputChange('builderName', e.target.value)}
            />
          </div>
          
          <div className="form-group">
            <label>Project Name:</label>
            <input
              type="text"
              value={formData.projectName}
              onChange={(e) => handleInputChange('projectName', e.target.value)}
            />
          </div>
          
          <div className="form-group">
            <label>Project Location:</label>
            <input
              type="text"
              value={formData.projectLocation}
              onChange={(e) => handleInputChange('projectLocation', e.target.value)}
            />
          </div>
          
          <div className="form-row">
            <div className="form-group">
              <label>City:</label>
              <input
                type="text"
                value={formData.city}
                onChange={(e) => handleInputChange('city', e.target.value)}
              />
            </div>
            
            <div className="form-group">
              <label>ZIP:</label>
              <input
                type="text"
                value={formData.zip}
                onChange={(e) => handleInputChange('zip', e.target.value)}
              />
            </div>
          </div>
          
          <div className="form-row">
            <div className="form-group">
              <label>Asset Type:</label>
              <select
                value={formData.assetType}
                onChange={(e) => handleInputChange('assetType', e.target.value)}
              >
                <option value="SFR">SFR</option>
                <option value="Multi-Family">Multi-Family</option>
              </select>
            </div>
            
            <div className="form-group">
              <label>Loan Type:</label>
              <select
                value={formData.loanType}
                onChange={(e) => handleInputChange('loanType', e.target.value)}
              >
                <option value="Ground Up">Ground Up</option>
                <option value="Renovation">Renovation</option>
              </select>
            </div>
          </div>
          
          <div className="form-group">
            <label>Unit Count:</label>
            <input
              type="number"
              value={formData.unitCount}
              onChange={(e) => handleInputChange('unitCount', parseInt(e.target.value) || 1)}
            />
          </div>

          <h3>Financial Details</h3>
          
          <div className="form-group">
            <label>Refinance Costs (Total Construction Cost):</label>
            <input
              type="number"
              value={formData.refinanceCosts}
              onChange={(e) => handleInputChange('refinanceCosts', parseFloat(e.target.value) || 0)}
            />
          </div>
          
          <div className="form-group">
            <label>Total Equity:</label>
            <input
              type="number"
              value={formData.totalEquity}
              onChange={(e) => handleInputChange('totalEquity', parseFloat(e.target.value) || 0)}
            />
          </div>
          
          <div className="form-group">
            <label>Equity Source (Land, Costs Paid to Date, etc.):</label>
            <input
              type="number"
              value={formData.equitySource}
              onChange={(e) => handleInputChange('equitySource', parseFloat(e.target.value) || 0)}
            />
          </div>
          
          <div className="form-group">
            <label>Desired LTC:</label>
            <input
              type="number"
              step="0.01"
              max="1"
              min="0"
              value={formData.desiredLTC}
              onChange={(e) => handleInputChange('desiredLTC', parseFloat(e.target.value) || 0)}
            />
          </div>
          
          <div className="form-group">
            <label>ARV (After Repair Value):</label>
            <input
              type="number"
              value={formData.arv}
              onChange={(e) => handleInputChange('arv', parseFloat(e.target.value) || 0)}
            />
          </div>

          <h3>Construction Costs Breakdown</h3>
          
          <div className="form-group">
            <label>Land Cost:</label>
            <input
              type="number"
              value={formData.landCost}
              onChange={(e) => handleInputChange('landCost', parseFloat(e.target.value) || 0)}
            />
          </div>
          
          <div className="form-group">
            <label>Soft Costs:</label>
            <input
              type="number"
              value={formData.softCosts}
              onChange={(e) => handleInputChange('softCosts', parseFloat(e.target.value) || 0)}
            />
          </div>
          
          <div className="form-group">
            <label>Hard Costs:</label>
            <input
              type="number"
              value={formData.hardCosts}
              onChange={(e) => handleInputChange('hardCosts', parseFloat(e.target.value) || 0)}
            />
          </div>
          
          <div className="form-group">
            <label>Title / Closing Allowance:</label>
            <input
              type="number"
              value={formData.titleClosingAllowance}
              onChange={(e) => handleInputChange('titleClosingAllowance', parseFloat(e.target.value) || 0)}
            />
          </div>

          <h3>Financing Terms</h3>
          
          <div className="form-group">
            <label>Origination Points:</label>
            <input
              type="number"
              step="0.01"
              value={formData.originationPoints}
              onChange={(e) => handleInputChange('originationPoints', parseFloat(e.target.value) || 0)}
            />
          </div>
          
          <div className="form-group">
            <label>Loan Interest Rate:</label>
            <input
              type="number"
              step="0.01"
              value={formData.loanInterestRate}
              onChange={(e) => handleInputChange('loanInterestRate', parseFloat(e.target.value) || 0)}
            />
          </div>
          
          <div className="form-group">
            <label>Term (months):</label>
            <input
              type="number"
              value={formData.term}
              onChange={(e) => handleInputChange('term', parseInt(e.target.value) || 12)}
            />
          </div>
          
          <div className="form-group">
            <label>Utilization Rate:</label>
            <input
              type="number"
              step="0.1"
              max="1"
              min="0"
              value={formData.utilizationRate}
              onChange={(e) => handleInputChange('utilizationRate', parseFloat(e.target.value) || 0)}
            />
          </div>

          <h3>Borrower Information</h3>
          
          <div className="form-group">
            <label>Estimated Credit Score:</label>
            <input
              type="number"
              value={formData.creditScore}
              onChange={(e) => handleInputChange('creditScore', parseInt(e.target.value) || 0)}
            />
          </div>
          
          <div className="form-group">
            <label>Gross/Stated Liquidity:</label>
            <input
              type="number"
              value={formData.grossLiquidity}
              onChange={(e) => handleInputChange('grossLiquidity', parseFloat(e.target.value) || 0)}
            />
          </div>
          
          <div className="form-group">
            <label>Stated Net Worth:</label>
            <input
              type="number"
              value={formData.statedNetWorth}
              onChange={(e) => handleInputChange('statedNetWorth', parseFloat(e.target.value) || 0)}
            />
          </div>
        </div>

        <div className="results-section">
          <h2>Calculated Results</h2>
          
          <div className="results-group">
            <h3>Equity Calculator</h3>
            <div className="result-item">
              <span className="label">Total Equity Needed:</span>
              <span className="value">{formatCurrency(calculations.totalEquityNeeded)}</span>
            </div>
            <div className="result-item">
              <span className="label">Additional Equity Needed:</span>
              <span className={`value ${calculations.additionalEquityNeeded < 0 ? 'positive' : 'negative'}`}>
                {formatCurrency(calculations.additionalEquityNeeded)}
              </span>
            </div>
            <div className="result-item">
              <span className="label">Project LTC:</span>
              <span className="value">{formatPercentage(calculations.projectLTC)}</span>
            </div>
            <div className="result-item">
              <span className="label">Funding Gap:</span>
              <span className="value">{formatCurrency(calculations.fundingGap)}</span>
            </div>
          </div>

          <div className="results-group">
            <h3>Financing Details</h3>
            <div className="result-item">
              <span className="label">Estimated Loan Amount:</span>
              <span className="value">{formatCurrency(calculations.estimatedLoanAmount)}</span>
            </div>
            <div className="result-item">
              <span className="label">Origination Fees:</span>
              <span className="value">{formatCurrency(calculations.originationFees)}</span>
            </div>
            <div className="result-item">
              <span className="label">Interest Reserve (Funded):</span>
              <span className="value">{formatCurrency(calculations.interestReserve)}</span>
            </div>
            <div className="result-item">
              <span className="label">Total Loan Costs:</span>
              <span className="value">{formatCurrency(calculations.totalLoanCosts)}</span>
            </div>
            <div className="result-item">
              <span className="label">CoFi Advance Day 1:</span>
              <span className="value">{formatCurrency(calculations.cofiAdvanceDay1)}</span>
            </div>
          </div>

          <div className="results-group">
            <h3>Project Analysis</h3>
            <div className="result-item">
              <span className="label">Total Project Cost:</span>
              <span className="value">{formatCurrency(calculations.totalProjectCost)}</span>
            </div>
            <div className="result-item">
              <span className="label">Gross Project Revenue:</span>
              <span className="value">{formatCurrency(calculations.grossProjectRevenue)}</span>
            </div>
            <div className="result-item">
              <span className="label">Estimated Project Revenue:</span>
              <span className="value">{formatCurrency(calculations.estimatedProjectRevenue)}</span>
            </div>
            <div className="result-item">
              <span className="label">Estimated Profit Margin:</span>
              <span className="value">{formatPercentage(calculations.estimatedProfitMargin)}</span>
            </div>
          </div>

          <div className="results-group">
            <h3>Ratios</h3>
            <div className="result-item">
              <span className="label">Financed LTC:</span>
              <span className="value">{formatPercentage(calculations.financedLTC)}</span>
            </div>
            <div className="result-item">
              <span className="label">Financed LTV:</span>
              <span className="value">{formatPercentage(calculations.financedLTV)}</span>
            </div>
          </div>

          <div className="results-group">
            <h3>CoFi Fees</h3>
            <div className="result-item">
              <span className="label">Total CoFi Fees:</span>
              <span className="value">{formatCurrency(calculations.cofiFees)}</span>
            </div>
            <div className="fee-breakdown">
              <div className="fee-item">$1,000 Doc Fee + Credit Pull + Background Check</div>
              <div className="fee-item">${250 * formData.unitCount} Draw Fee ({formData.unitCount} units × $250)</div>
              <div className="fee-item">$500 Legal + UW Fee + Technology and Camera</div>
            </div>
          </div>

          <div className="buy-box-checks">
            <h3>Buy Box Requirements</h3>
            <div className={`check-item ${formData.creditScore >= 660 ? 'pass' : 'fail'}`}>
              Credit Score: {formData.creditScore} (Need 660+)
            </div>
            <div className={`check-item ${calculations.financedLTC <= 0.85 ? 'pass' : 'fail'}`}>
              LTC: {formatPercentage(calculations.financedLTC)} (Need ≤85%)
            </div>
            <div className={`check-item ${calculations.financedLTV <= 0.75 ? 'pass' : 'fail'}`}>
              LTV: {formatPercentage(calculations.financedLTV)} (Need ≤75%)
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default App;